import React from 'react';
import { motion } from 'framer-motion';

const experiences = [
  {
    company: 'Questera AI',
    role: 'Full Stack Developer',
    period: '08/2023 - Present',
    duration: 'Remote',
    description: 'Spearheading the development of scalable web applications and AI-driven products using Reactjs and Node.js.',
    achievements: [
      'Frontend & Backend: Building responsive, high-performance user interfaces in Reactjs while architecting scalable backend services using Node.js, MongoDB, and RabbitMQ.',
      'AI & Data Engineering: Developed AI Agents capable of connecting with multiple 3rd party integrations, understanding user intent, and executing automated workflows using LLM-powered reasoning.',
      'AI Agent - GIA: Built Graphical Interpretation Agent (GIA) that integrates with Snowflake & Big Query to generate AI-powered insights & charts dynamically.',
      'Stripe Integration: Led Stripe subscription billing implementation, enabling automated recurring payments and complex plan management for 20K+ users.',
      'Clients Success & Delivery: Collaborated directly with Clients to gather requirements, provide technical guidance, and ensure timely delivery of product features.'
    ],
    tags: ['Reactjs', 'Node.js', 'MongoDB', 'RabbitMQ', 'AI Agents', 'Stripe']
  },
  {
    company: 'Tech Mahindra',
    role: 'Subject Matter Expert - SME',
    period: '01/2021 - 08/2023',
    duration: 'Chennai, India',
    description: 'Lead the team and served as Subject Matter Expert for Telstra products.',
    achievements: [
      'Supervised and guided the support team, ensuring accuracy in shared information.',
      'Handled customer queries regarding technical issues of Telstra products.',
      'Provided process training and mentorship to new team members, ensuring smooth onboarding.'
    ],
    tags: ['Customer Success', 'Team Leadership', 'Mentorship']
  },
  {
    company: 'Sutherland',
    role: 'Technical Support Associate',
    period: '10/2019 - 12/2020',
    duration: 'Chennai, India',
    description: 'Provided technical and non-technical support and assistance via live chat.',
    achievements: [
      'Assisted customers with tech and non-tech queries via live chat.',
      'Troubleshot and resolved customer issues efficiently, ensuring high satisfaction.',
      'Developed deep empathy for end-users, forming the foundation of my user-centric approach to software engineering.'
    ],
    tags: ['Customer Service', 'Troubleshooting', 'Problem Solving']
  }
];

const Experience = () => {
  return (
    <section id="experience" className="py-24 bg-slate-950">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Professional Journey</h2>
          <p className="text-slate-400">From frontline support to building the very systems I used to troubleshoot.</p>
        </motion.div>

        <div className="relative border-l border-slate-800 ml-3 md:ml-6 space-y-12">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative pl-8 md:pl-12"
            >
              <div className="absolute -left-[9px] top-0 p-1 bg-slate-950 border border-indigo-500 rounded-full">
                <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              </div>

              <div className="glass-card p-6 md:p-8 rounded-2xl">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                  <div>
                    <h3 className="text-xl font-bold text-white">{exp.role}</h3>
                    <div className="text-indigo-400 font-medium">{exp.company}</div>
                  </div>
                  <div className="text-slate-500 text-sm font-mono bg-slate-950/50 px-3 py-1 rounded-full border border-slate-800 w-fit">
                    {exp.period} • {exp.duration}
                  </div>
                </div>

                <p className="text-slate-300 font-medium mb-4">
                  {exp.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {exp.achievements.map((ach, i) => (
                    <li key={i} className="text-slate-400 text-sm flex items-start gap-3">
                      <span className="text-indigo-500 mt-0.5">▹</span>
                      <span className="leading-relaxed">{ach}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-2">
                  {exp.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-slate-800/50 text-slate-300 text-xs rounded-full border border-slate-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;